import './styles.scss'
import { FieldWithLabel } from 'components/FieldWithLabel/FieldWithLabel'
import Collapse from 'components/Collapse/Collapse'
import { Collapse as CollapseAntd } from 'antd'
import { useMemo, useState } from 'react'
import { Attachment } from 'components/Attachment/Attachment'
import { Tooltip } from 'components/Tooltip/Tooltip'
import { Avatar } from 'components/Avatar/Avatar'
import Button from 'components/Button/Button'
import { Typography } from 'components/Typography'
import { BUTTON_TYPES } from 'components/Button'

import { ReactComponent as Delete24 } from 'assets/svg/Delete24.svg'
import { BTN_TXT } from 'constants/txt'
import moment from 'moment'
import {
  timelineDateFormat,
  timelineTimeFormat
} from 'components/Timelines/constants'
import { fileNameFormatter, getCapitalCharName } from 'helper/common'
import usePopup from 'hooks/usePopup'
import { TAttachmentsListWithExpandProps } from '../types'
import ConfirmDeletionPopup from './components/ConfirmDeletionPopup'
import VersionHistoryPopup from './components/VersionHistoryPopup'
import ContractVersionHistory from 'features/ContractDetails/ContractDetailsGeneral/ContractDetailsDocuments/ContractVersionHistory/ContractVersionHistory'
import { TRFPDocument } from 'features/RFP/RFPDetails/types'
import ContractSignatoryUsers from 'features/ContractDetails/ContractDetailsGeneral/ContractDetailsDocuments/ContractSignatoryUsers/ContractSignatoryUsers'
import { Status } from 'components/Status'

const { Panel } = CollapseAntd

const NON_EXPANDED_AMOUNT = 3

const WithExpand = ({
  expandedFiles,
  label,
  infoTooltipText,
  renderActionButtonComponent,
  renderBannerComponent,
  deleteButton,
  hasSignatoriesSection,
  children
}: TAttachmentsListWithExpandProps) => {
  const [activeKey, setActiveKey] = useState<string | string[]>(['0'])
  const [isExpanded, setIsExpanded] = useState(false)

  const versionHistoryPopupProps = usePopup<{ index: number }>({
    title: 'Version history'
  })
  const confirmDeletePopupProps = usePopup<{ fileUuid: string }>({
    title: 'Delete file?'
  })

  const expandButton = useMemo(() => {
    if (expandedFiles && expandedFiles?.length <= NON_EXPANDED_AMOUNT) {
      return null
    }
    return (
      <Button
        className="ml-16 mb-8"
        type={BUTTON_TYPES.LINK}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        {isExpanded
          ? BTN_TXT.SHOW_FEWER_CONTRACTS
          : `Show all ${expandedFiles?.length} contracts`}
      </Button>
    )
  }, [expandedFiles, isExpanded])

  // unite latest file and archived files into one array
  const historyFiles = useMemo(() => {
    const history: TRFPDocument[] = []
    const index = versionHistoryPopupProps.state.payload?.index

    if (index !== undefined) {
      const file = expandedFiles?.[index]
      if (file) {
        history.push(file)
        history.push(...(file.history ?? []))
      }
    }
    return history
  }, [expandedFiles, versionHistoryPopupProps.state.payload?.index])

  const panels = useMemo(
    () =>
      expandedFiles
        ?.slice(0, isExpanded ? undefined : NON_EXPANDED_AMOUNT)
        .map((file, index) => {
          const isControlButtonsSectionAvailable =
            !!renderActionButtonComponent ||
            !!deleteButton ||
            !!file?.history?.length

          return (
            <Panel
              key={index}
              header={
                <div className="attachments-panel-header">
                  <Attachment
                    filename={fileNameFormatter(file.file)}
                    link={file.file ?? ''}
                  />
                  {!!file?.required_action && (
                    <Status.Document status={file.required_action} />
                  )}
                </div>
              }
              extra={
                <Tooltip
                  title={`Uploaded by ${file.creator?.first_name} ${file.creator?.last_name}`}
                  className="mr-48"
                  placement="topLeft"
                >
                  <Avatar
                    name={`${getCapitalCharName(
                      file.creator?.first_name ?? '',
                      file.creator?.last_name ?? ''
                    )}`}
                    alt="User avatar"
                    src={file.creator?.avatar?.file_preview ?? ''}
                  />
                </Tooltip>
              }
            >
              <div className="full-width column attachments-panel-details">
                {!!renderBannerComponent && renderBannerComponent(file.uuid)}
                {children}
                <div className="grid align-center">
                  <Typography.Body2 className="mr-16 attachments-panel-details__uploaded">
                    Uploaded
                  </Typography.Body2>
                  <Typography.Body2>
                    {`${moment(file.created_at).format(
                      timelineDateFormat
                    )} · ${moment(file.created_at).format(timelineTimeFormat)}`}
                  </Typography.Body2>
                </div>
                {hasSignatoriesSection?.(file) && (
                  <ContractSignatoryUsers file={file} />
                )}
                {isControlButtonsSectionAvailable && (
                  <div className="row space-between mt-16">
                    <div className="row gap-8 align-end">
                      {!!renderActionButtonComponent &&
                        renderActionButtonComponent(file.uuid)}
                      {!!file?.history?.length && file?.history?.length > 0 && (
                        <Button
                          onClick={() =>
                            versionHistoryPopupProps.actions.open({ index })
                          }
                          type={BUTTON_TYPES.LINK}
                          medium
                          upperCase
                        >
                          {BTN_TXT.VERSION_HISTORY}
                        </Button>
                      )}
                    </div>
                    {!!deleteButton && file.can_delete && (
                      <Button
                        type={BUTTON_TYPES.LINK}
                        icon={<Delete24 />}
                        onClick={() => {
                          if (!!deleteButton.onClick) {
                            confirmDeletePopupProps.actions.open({
                              fileUuid: file.uuid
                            })
                          }
                        }}
                        disabled={!deleteButton.enabled}
                      />
                    )}
                  </div>
                )}
              </div>
            </Panel>
          )
        }),
    [
      renderActionButtonComponent,
      renderBannerComponent,
      children,
      confirmDeletePopupProps.actions,
      deleteButton,
      expandedFiles,
      hasSignatoriesSection,
      isExpanded,
      versionHistoryPopupProps.actions
    ]
  )

  return (
    <FieldWithLabel title={label} infoTooltipText={infoTooltipText}>
      {expandedFiles && expandedFiles?.length > 0 && (
        <Collapse.Attachment
          activeKey={activeKey}
          expandIconPosition="right"
          onChange={setActiveKey}
        >
          {panels}
        </Collapse.Attachment>
      )}
      {expandButton}
      <VersionHistoryPopup
        {...versionHistoryPopupProps.state}
        {...versionHistoryPopupProps.actions}
        children={<ContractVersionHistory documents={historyFiles} />}
      />
      <ConfirmDeletionPopup
        {...confirmDeletePopupProps.state}
        {...confirmDeletePopupProps.actions}
        onCancelPress={() => {
          confirmDeletePopupProps.actions.close()
        }}
        onDeletePress={() => {
          confirmDeletePopupProps.actions.close()
          deleteButton?.onClick(
            confirmDeletePopupProps.state.payload?.fileUuid ?? ''
          )
          setActiveKey([])
        }}
      />
    </FieldWithLabel>
  )
}

export default WithExpand
